import {
  serviceElement,
  type AppliesTo,
  type WithElement,
} from '@obraplan/core/domain/takeoff/applies-to';
import type { CatalogDiscipline } from '@obraplan/core/domain/takeoff/service-catalog';
import { steelRatesFor, type SteelRate } from '@obraplan/core/domain/takeoff/steel';

/**
 * A taxa da disciplina por elemento na bancada (Fase 42, etapa 3 — G4, RN-573, ADR-189).
 *
 * A disciplina cujos serviços de aço medem mais de um elemento — a PILARES DE CONTENÇÃO, com o
 * muro e o pilar; a fundação, com o bloco e o baldrame — guarda uma lista por elemento, além da
 * dela. O serviço lê a do elemento que mede; sem ela, a da disciplina inteira (`steelRatesFor`).
 *
 * As ações de taxa (`SET_STEEL_RATES`, `APPLY_STEEL_AVERAGE`) dizem o elemento quando é a lista
 * dele que trocam; sem elemento, é a da disciplina inteira, como sempre.
 */

/**
 * A taxa que um serviço lê na disciplina dele: a lista do elemento que ele mede, quando a
 * disciplina tem uma; sem ela, a da disciplina inteira. É o que a tela pendura no serviço antes de
 * perguntar ao desenho (`withSteelRate`), e o que a taxa da fundação completa (RN-545).
 */
export function taxaDoServico(
  disciplina: Pick<CatalogDiscipline, 'steelRates' | 'elementSteelRates'>,
  servico: WithElement,
): readonly SteelRate[] | undefined {
  return steelRatesFor(disciplina, serviceElement(servico));
}

/**
 * A lista que uma ação de taxa troca: sem elemento, a da disciplina inteira; com ele, a que o
 * elemento lê — a própria, ou, sem ela, a da disciplina, de onde a própria nasce copiada.
 */
export function listaDaTaxa(
  disciplina: Pick<CatalogDiscipline, 'steelRates' | 'elementSteelRates'>,
  element: AppliesTo | undefined,
): readonly SteelRate[] | undefined {
  return steelRatesFor(disciplina, element);
}

/**
 * A disciplina com a lista trocada. Sem elemento, a da disciplina inteira. Com elemento, a lista
 * dele, no mesmo lugar, ou no fim quando nasce; a lista vazia do elemento sai, e ele volta a ler
 * a da disciplina — o campo fica, vazio, para a gravação apagar a do servidor.
 */
export function comAListaDaTaxa(
  disciplina: CatalogDiscipline,
  element: AppliesTo | undefined,
  rates: readonly SteelRate[],
): CatalogDiscipline {
  if (element === undefined) return { ...disciplina, steelRates: rates };
  const listas = disciplina.elementSteelRates ?? [];
  const tinha = listas.some((lista) => lista.element === element);
  if (rates.length === 0) {
    return tinha
      ? { ...disciplina, elementSteelRates: listas.filter((lista) => lista.element !== element) }
      : disciplina;
  }
  return {
    ...disciplina,
    elementSteelRates: tinha
      ? listas.map((lista) => (lista.element === element ? { element, rates } : lista))
      : [...listas, { element, rates }],
  };
}

/** O elemento tem a lista própria — não vazia — nesta disciplina. */
export function temListaPropria(
  disciplina: Pick<CatalogDiscipline, 'elementSteelRates'>,
  element: AppliesTo,
): boolean {
  return (
    disciplina.elementSteelRates?.some(
      (lista) => lista.element === element && lista.rates.length > 0,
    ) ?? false
  );
}
